import { CheckCircle2, Clock3, XCircle } from "lucide-react";

export type WappTemplatesStatus =
  | "approved"
  | "pending"
  | "rejected"
  | "missing"
  | "unknown"
  | null
  | undefined;

type Tr = (es: string, en: string) => string;

type WhatsAppTemplatesStatusNoticeProps = {
  tr: Tr;
  status: WappTemplatesStatus;
  /** When false, hide the approved success state (connected + ready is enough). */
  showWhenApproved?: boolean;
};

function noticeCopy(
  tr: Tr,
  status: Exclude<WappTemplatesStatus, null | undefined>,
): { title: string; body: string; tone: "pending" | "ok" | "error" } {
  if (status === "approved") {
    return {
      tone: "ok",
      title: tr("Plantillas aprobadas", "Templates approved"),
      body: tr(
        "Meta ya aprobó las plantillas. Eloria puede escribir a cualquier contacto, incluso si no te escribieron en las últimas 24 horas.",
        "Meta has approved the templates. Eloria can message any contact, even if they have not written in the last 24 hours.",
      ),
    };
  }
  if (status === "rejected") {
    return {
      tone: "error",
      title: tr("Plantillas rechazadas", "Templates rejected"),
      body: tr(
        "Meta rechazó una o más plantillas. Hasta que se resuelva, Eloria solo puede escribir a contactos que te hayan escrito en las últimas 24 horas.",
        "Meta rejected one or more templates. Until this is fixed, Eloria can only message contacts who wrote to you in the last 24 hours.",
      ),
    };
  }
  // pending / missing / unknown all mean "not ready yet"
  return {
    tone: "pending",
    title: tr(
      "Plantillas aún no listas",
      "Templates not ready yet",
    ),
    body: tr(
      "Las plantillas de este número todavía no están listas (configuración o revisión de Meta). Mientras tanto, Eloria solo puede escribir a contactos que te hayan escrito en las últimas 24 horas. Te avisaremos por WhatsApp cuando queden aprobadas.",
      "This number's templates are not ready yet (setup or Meta review). Until then, Eloria can only message contacts who wrote to you in the last 24 hours. We will notify you on WhatsApp when they are approved.",
    ),
  };
}

export function WhatsAppTemplatesStatusNotice({
  tr,
  status,
  showWhenApproved = false,
}: WhatsAppTemplatesStatusNoticeProps) {
  if (!status) {
    return null;
  }
  if (status === "approved" && !showWhenApproved) {
    return null;
  }

  const { title, body, tone } = noticeCopy(tr, status);
  const styles =
    tone === "ok"
      ? "border-emerald-200 bg-emerald-50 text-emerald-950"
      : tone === "error"
        ? "border-red-200 bg-red-50 text-red-950"
        : "border-amber-200 bg-amber-50 text-amber-950";
  const Icon =
    tone === "ok" ? CheckCircle2 : tone === "error" ? XCircle : Clock3;
  const iconClass =
    tone === "ok"
      ? "text-emerald-600"
      : tone === "error"
        ? "text-red-600"
        : "text-amber-600";

  return (
    <div
      className={`mt-3 rounded-lg border px-3 py-2.5 ${styles}`}
      role="status"
      data-templates-status={status}
    >
      <div className="flex items-start gap-2">
        <Icon
          className={`mt-0.5 h-4 w-4 shrink-0 ${iconClass}`}
          strokeWidth={2}
          aria-hidden
        />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-medium leading-snug">{title}</p>
          <p className="text-xs leading-snug opacity-90">{body}</p>
        </div>
      </div>
    </div>
  );
}

export function templatesStatusNeedsAttention(
  status: WappTemplatesStatus,
): boolean {
  return Boolean(status && status !== "approved");
}
